import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

import type { SiteStatus, SurveyReportStatus } from '../api/types'
import type { CoverageGroupBy, CoverageListMode } from '../lib/coverage-board'

/**
 * Shared UI state for the GIS Coverage board (V5 toolbar layout — the Coverage twin of
 * `gisIssueViewStore`): the survey-status facet + officer facet + free-text query behind the
 * toolbar dropdowns, the list grouping, the Sites/Officers/Surveys view, and the Surveys view's
 * pending/missed toggle. The map's site dots read `statusFilter`, so dots and list cross-filter in
 * lockstep. Multi-select facets: arrays (`[]` = all), values union (OR), facets AND. Ephemeral —
 * intentionally NOT persisted; `CoverageDashboard` resets on unmount so no other tab inherits a
 * filtered map.
 */
interface GisCoverageViewState {
  statusFilter: readonly SiteStatus[]
  /** Officer facet — officer display names; 'Unassigned' buckets the null-officer sites. */
  officerFilter: readonly string[]
  /** Free-text search over site name / officer / mandal. */
  query: string
  groupBy: CoverageGroupBy
  listMode: CoverageListMode
  /** The Surveys view's pending/missed toggle (shared so the toolbar Export matches the table). */
  surveyStatus: SurveyReportStatus
}

interface GisCoverageViewStore extends GisCoverageViewState {
  /** Toggle a status in/out of the facet (add if absent, remove if present). */
  toggleStatusFilter: (status: SiteStatus) => void
  /** Clear the status facet (back to "all"). */
  clearStatusFilter: () => void
  toggleOfficerFilter: (officer: string) => void
  setQuery: (query: string) => void
  setGroupBy: (groupBy: CoverageGroupBy) => void
  setListMode: (mode: CoverageListMode) => void
  setSurveyStatus: (status: SurveyReportStatus) => void
  /** Clear every toolbar filter (status + officer + query); keeps view/grouping. */
  clearFilters: () => void
  reset: () => void
}

const initialState: GisCoverageViewState = {
  statusFilter: [],
  officerFilter: [],
  query: '',
  groupBy: 'status', // triage default: worst sections (Overdue → Never surveyed → …) lead
  listMode: 'sites',
  surveyStatus: 'missed',
}

/** Add `value` to `arr` if missing, else drop it — the toggle spine the facets share. */
function toggleValue<T>(arr: readonly T[], value: T): T[] {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]
}

export const useGisCoverageView = create<GisCoverageViewStore>()(
  devtools(
    immer((set) => ({
      ...initialState,

      toggleStatusFilter: (status) =>
        set((s) => {
          s.statusFilter = toggleValue(s.statusFilter, status)
        }),

      clearStatusFilter: () =>
        set((s) => {
          s.statusFilter = []
        }),

      toggleOfficerFilter: (officer) =>
        set((s) => {
          s.officerFilter = toggleValue(s.officerFilter, officer)
        }),

      setQuery: (query) =>
        set((s) => {
          s.query = query
        }),

      setGroupBy: (groupBy) =>
        set((s) => {
          s.groupBy = groupBy
        }),

      setListMode: (mode) =>
        set((s) => {
          s.listMode = mode
        }),

      setSurveyStatus: (status) =>
        set((s) => {
          s.surveyStatus = status
        }),

      clearFilters: () =>
        set((s) => {
          s.statusFilter = []
          s.officerFilter = []
          s.query = ''
        }),

      reset: () => set(() => ({ ...initialState })),
    })),
    { name: 'useGisCoverageView' },
  ),
)
